// docs/146 — gear while a company is outfitting (the World lobby, or arriving in a begun World
// before Set out): the inventory works as everywhere else, except that nothing is sold, players do
// not trade with one another yet, and a hero's starting kit (remade by Edit) stays in the company.
// The module (`founding.rs`, `items.rs`) is the judge; this only lets the inventory say so first.
import { useMemo } from 'react';
import { identityHex } from '../../core/net/connection';
import { useSession } from '../../core/stores/session';
import { useFoundingKit, useFoundingPurses, useVillagePresence, useWorldSettings } from '../../core/subscriptions/hooks';
import { outfittingWindow } from '../screens/founding';

export interface Outfitting {
  /** The World lobby is still gathering. */
  gathering: boolean;
  /** I am outfitting here. */
  mine: boolean;
  /** Whether the player with this identity (hex) is outfitting here. */
  of(hex: string): boolean;
  /** A bound kit piece's item id → the hero whose kit it is. */
  kit: Map<bigint, bigint>;
  /** Nothing is sold here now: I outfit, and not in a village (whose counter still buys). */
  noSelling: boolean;
}

export function useOutfitting(worldId: bigint | undefined): Outfitting {
  const me = useSession((s) => s.identity ?? '');
  const settings = useWorldSettings(worldId);
  const purses = useFoundingPurses(worldId);
  const kitRows = useFoundingKit(worldId);
  const inVillage = useVillagePresence(worldId).some((p) => identityHex(p.owner) === me && p.villageId !== 0n);
  return useMemo(() => {
    const phase = settings?.phase;
    const open = new Set(purses.filter((p) => p.open).map((p) => identityHex(p.owner)));
    const of = (hex: string) => outfittingWindow(phase, open.has(hex) ? { open: true } : undefined);
    return { gathering: phase === 0, mine: of(me), of, kit: new Map(kitRows.map((k) => [k.itemId, k.heroId])), noSelling: of(me) && !inVillage };
  }, [settings?.phase, purses, kitRows, me, inVillage]);
}
